/**
 * Accesso al tema dalla parte React dell'applicazione.
 *
 * Il contesto sta in un file suo, e non dentro `InstitorConfigProvider.tsx`,
 * per una ragione pratica: il selettore del tema è montato nell'intestazione,
 * cioè **dentro** il provider, e importarlo dal file del provider creerebbe un
 * ciclo fra i due moduli. Un contesto in mezzo lo evita e costa un file.
 *
 * Fuori dal provider l'hook solleva invece di restituire un valore di ripiego:
 * un componente che crede di poter cambiare tema e non può è un difetto
 * silenzioso, e un difetto silenzioso in un contesto è il più difficile da
 * trovare.
 */

import { createContext, useContext } from 'react';

import type { ResolvedTheme, ThemeMode } from './themeMode';

/** Tema attivo e comando per cambiarlo. */
export interface ThemeModeValue {
  /** Stato scelto dall'utente: «sistema», «chiaro» o «scuro». */
  mode: ThemeMode;
  /** Tema effettivamente applicato, una volta risolto «sistema». */
  resolved: ResolvedTheme;
  /**
   * Cambia il tema e ne ricorda la scelta.
   *
   * @param mode Nuovo stato scelto dall'utente.
   */
  setMode: (mode: ThemeMode) => void;
  /** True se le tabelle usano righe più basse (preferenza del browser, ADR 0030). */
  compactTables: boolean;
  /**
   * Accende o spegne le tabelle compatte e ne ricorda la scelta.
   *
   * @param compact True per le tabelle compatte.
   */
  setCompactTables: (compact: boolean) => void;
}

/** Contesto del tema; vale `null` fuori da `InstitorConfigProvider`. */
export const ThemeModeContext = createContext<ThemeModeValue | null>(null);

/**
 * Legge il tema attivo e il comando per cambiarlo.
 *
 * @returns Stato scelto, tema applicato e funzione di cambio.
 * @throws {Error} Se chiamato fuori da `InstitorConfigProvider`.
 */
export function useThemeMode(): ThemeModeValue {
  const value = useContext(ThemeModeContext);
  if (value === null) {
    throw new Error('useThemeMode va usato dentro InstitorConfigProvider');
  }
  return value;
}
